/**
 * Compiler-only marker protocol carried by deterministic HTML imports.
 *
 * These names choose the production render lane and preserve source metadata
 * across JSX storage. Every producer and consumer uses this module so a
 * protocol change cannot silently strand persisted imports in the authored
 * email renderer.
 */
export const IMPORT_PROVENANCE = {
  prefix: 'data-brew-import',
  root: 'data-brew-import',
  doctype: 'data-brew-import-doctype',
  hasLang: 'data-brew-import-has-lang',
  hasDir: 'data-brew-import-has-dir',
  emptySrc: 'data-brew-import-empty-src',
  sourcePreload: 'data-brew-import-source-preload',
  comment: 'data-brew-import-comment',
} as const

export function isDeterministicImportJsx(jsx: string): boolean {
  return new RegExp(`\\b${IMPORT_PROVENANCE.root}=(?:"1"|\\{"1"\\})`).test(jsx)
}

/**
 * Cheap whole-string scan for the import DIALECT's lowercase `<body` tag —
 * the same signal `patchSourceAwareImportedJsx` engages on. Authored emails
 * carry capital `<Body`, so this is a fast prefilter/approximation; the
 * authoritative predicate is `isImportDialectEmailJsx` (parses and checks
 * the actual body opening tag). Shared by the predicate's prefilter and the
 * Convex dialect-orphan audit's table scan.
 */
export const IMPORT_DIALECT_BODY_SCAN_PATTERN = /<body[\s>]/

export function encodedImportDoctypeFromHtml(html: string): string | undefined {
  return html.match(
    new RegExp(`\\s${IMPORT_PROVENANCE.doctype}="([^"]*)"`)
  )?.[1]
}

export function hasImportRootAttributeMarker(
  html: string,
  name: 'lang' | 'dir'
): boolean {
  const marker =
    name === 'lang' ? IMPORT_PROVENANCE.hasLang : IMPORT_PROVENANCE.hasDir
  return html.includes(`${marker}="1"`)
}

export function stripImportRootMetadata(html: string): string {
  const names = [
    IMPORT_PROVENANCE.root,
    IMPORT_PROVENANCE.doctype,
    IMPORT_PROVENANCE.hasLang,
    IMPORT_PROVENANCE.hasDir,
  ].join('|')
  return html.replace(new RegExp(`\\s(?:${names})="[^"]*"`, 'g'), '')
}

/**
 * Strip EVERY `data-brew-import-*` provenance attribute from a JSX string.
 *
 * For reference/template JSX injected into authoring prompts and for
 * agent-authored output landing on a non-import lane: provenance markers
 * must never leak into a brand-new authored document — a stray root marker
 * flips the render lane into the strict deterministic emitter and
 * permanently misclassifies the row as an import. Handles both the
 * compiler's `="…"` string form and the `={"…"}` expression form a model
 * might emit.
 *
 * Known cosmetic edge: a TEXT literal that itself spells out a marker
 * attribute (copy discussing these internals) loses those words. The strip
 * is structurally safe either way, and such copy has no legitimate reason
 * to exist in a marketing email.
 */
export function stripImportProvenanceFromJsx(jsx: string): string {
  return jsx.replace(
    new RegExp(
      `\\s${IMPORT_PROVENANCE.prefix}(?:-[a-z-]+)?=(?:"[^"]*"|\\{"[^"]*"\\})`,
      'g'
    ),
    ''
  )
}
